/**
 * Bottom sheet on phones, centred dialog on wider screens (port of runebound.gg `ui/Sheet.tsx`), built
 * on the native `<dialog>` so focus trapping, Escape and backdrop clicks are browser-standard. In a match:
 * mulligan, choosing cards from a zone, deck viewer, settings (docs/design/06 §4.3).
 */
import { X } from 'lucide-react';
import type React from 'react';
import { useEffect, useId, useRef } from 'react';
import { cn } from './cn';

/** A modal sheet (bottom sheet on phones, dialog on wider screens). */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className,
  label,
}: {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode | undefined;
  children: React.ReactNode;
  footer?: React.ReactNode | undefined;
  size?: 'sm' | 'md' | 'lg' | 'full';
  className?: string | undefined;
  /** Accessible name when there is no visible title. */
  label?: string | undefined;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-2xl', full: 'sm:max-w-4xl' };
  return (
    <dialog
      ref={ref}
      aria-label={title === undefined ? label : undefined}
      aria-labelledby={title === undefined ? undefined : titleId}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      className="fixed inset-0 m-0 p-0 w-full h-full max-w-none max-h-none bg-transparent text-text open:flex items-end sm:items-center justify-center"
    >
      <div
        className={cn(
          'w-full max-h-[92dvh] flex flex-col bg-panel2 border border-border-strong shadow-[0_24px_80px_rgba(0,0,0,.6)]',
          'rounded-t-[22px] sm:rounded-[22px]',
          widths[size],
          className,
        )}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="sm:hidden mx-auto mt-2 w-10 h-1 rounded-full bg-border-strong" />
        {title !== undefined ? (
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <h2 id={titleId} className="display text-[20px] leading-[26px] font-bold">
              {title}
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="w-9 h-9 rounded-full inline-flex items-center justify-center text-muted hover:bg-pressed hover:text-text"
            >
              <X size={18} />
            </button>
          </div>
        ) : null}
        <div className="px-5 pb-5 overflow-y-auto flex-1">{children}</div>
        {footer ? <div className="px-5 py-4 border-t border-border">{footer}</div> : null}
      </div>
    </dialog>
  );
}

/** Confirm dialog, optionally destructive (concede, leave match). */
export function ConfirmSheet({
  open,
  onClose,
  title,
  message,
  confirmLabel = 'Confirm',
  destructive,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  message?: React.ReactNode | undefined;
  confirmLabel?: string | undefined;
  destructive?: boolean | undefined;
  onConfirm: () => void;
}) {
  return (
    <Sheet open={open} onClose={onClose} title={title} size="sm">
      {message ? <p className="text-[15px] text-muted mb-4">{message}</p> : null}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="h-11 px-4 rounded-[14px] border border-border-strong font-semibold hover:bg-pressed"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className={cn(
            'h-11 px-4 rounded-[14px] font-semibold',
            destructive ? 'bg-down text-white hover:bg-down/90' : 'bg-accent text-on-accent hover:bg-accent-hover',
          )}
        >
          {confirmLabel}
        </button>
      </div>
    </Sheet>
  );
}
